import { ShippingFlagKey } from "@redotech/feature-flags-keys-shipping/keys";
import {
  PermissionHandle,
  PermissionedRouteObject,
} from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { useShippingNewSettings } from "@redotech/redo-merchant-app-common/shipping-new-settings-context";
import { Fragment } from "react";
import { Outlet } from "react-router-dom";

type PermissionArguments = Parameters<
  Extract<PermissionHandle, (...args: never[]) => boolean>
>[0];

function permits(
  permission: PermissionHandle | undefined,
  args: PermissionArguments,
): boolean {
  if (typeof permission === "function") {
    return permission(args);
  }
  if (typeof permission === "string") {
    return args.userPermissions?.has(permission) === true;
  }
  return (
    !permission?.length ||
    permission.some((value) => args.userPermissions?.has(value))
  );
}

export function getShippingSettingsRoutePermission(
  current: PermissionedRouteObject | undefined,
  legacy: PermissionedRouteObject | undefined,
  enabled: boolean,
): PermissionHandle | false | undefined {
  const selected = enabled ? current : legacy;
  return selected ? selected.handle?.permission : false;
}

/** Keep one router tree, but preserve each rollout's route content and permissions. */
export function shippingSettingsFlagRoutes(
  current: PermissionedRouteObject[],
  legacy: PermissionedRouteObject[],
  currentRedirect?: PermissionedRouteObject,
): PermissionedRouteObject[] {
  const key = (route: PermissionedRouteObject) =>
    `${route.path?.replace(/\/\*$/, "") ?? ""}:${route.index === true}`;
  const oldByKey = new Map(legacy.map((route) => [key(route), route]));
  const nextByKey = new Map(current.map((route) => [key(route), route]));
  return [...new Set([...nextByKey.keys(), ...oldByKey.keys()])].map(
    (routeKey) => {
      const next = nextByKey.get(routeKey) ?? currentRedirect;
      const old = oldByKey.get(routeKey);
      if (next && next === old) {
        return next;
      }
      const source = next ?? old;
      if (!source) {
        throw new Error("Missing shipping settings route");
      }
      const {
        element: _element,
        Component: _Component,
        lazy: _lazy,
        children: _children,
        ...base
      } = source;
      const permission = (args: PermissionArguments) => {
        const selected = getShippingSettingsRoutePermission(
          next,
          old,
          args.team.featureFlags?.[ShippingFlagKey.SHIPPING_NEW_SETTINGS] ===
            true,
        );
        return selected !== false && permits(selected, args);
      };
      const route = {
        ...base,
        path: old ? old.path : next?.path,
        handle: {
          ...source.handle,
          permission,
          settingsPresentation: {
            redesigned: next?.handle,
            legacy: old?.handle,
          },
        },
        lazy: async () => {
          const [nextLoaded, separatelyLoadedOld] = await Promise.all([
            typeof next?.lazy === "function" ? next.lazy() : undefined,
            typeof old?.lazy === "function" && old.lazy !== next?.lazy
              ? old.lazy()
              : undefined,
          ]);
          const oldLoaded =
            old?.lazy === next?.lazy ? nextLoaded : separatelyLoadedOld;
          const Current = nextLoaded?.Component ?? next?.Component;
          const Legacy = oldLoaded?.Component ?? old?.Component;
          const currentElement = nextLoaded?.element ?? next?.element;
          const legacyElement = oldLoaded?.element ?? old?.element;
          function ShippingSettingsRoute() {
            const enabled = useShippingNewSettings();
            if (!(enabled ? next : old)) {
              return null;
            }
            const Component = enabled ? Current : Legacy;
            const element = enabled ? currentElement : legacyElement;
            return Component ? (
              <Component />
            ) : element !== undefined ? (
              <Fragment>{element}</Fragment>
            ) : (
              <Outlet />
            );
          }
          return { Component: ShippingSettingsRoute };
        },
      };
      return (old ?? next)?.index
        ? { ...route, index: true }
        : {
            ...route,
            index: false,
            children: shippingSettingsFlagRoutes(
              next?.children ?? [],
              old?.children ?? [],
              next?.path?.endsWith("/*") ? next : currentRedirect,
            ),
          };
    },
  );
}
